import type { LeaveStatus, LeaveTypeId } from '@lms/shared-types'
import { LEAVE_TYPE_STYLE } from './leave-type-palette'

/**
 * Display metadata for leave types. The visual fields (label, short, accent)
 * are derived from the canonical `leave-type-palette` source of truth so
 * every surface — calendar bars, command-palette widgets, badges — stays
 * visually aligned. Behavior-policy tables (`LEAVE_TYPE_HALF_DAY_ALLOWED`,
 * `LEAVE_TYPE_DOC_REQUIRED`) live here directly because they're not visual.
 *
 * New call sites should import `LEAVE_TYPE_STYLE` from `leave-type-palette`
 * directly. These re-exports exist for backward compatibility with existing
 * consumers and will likely be retired once the palette migration finishes.
 */

export const LEAVE_TYPE_LABEL: Record<LeaveTypeId, string> = Object.fromEntries(
  Object.entries(LEAVE_TYPE_STYLE).map(([k, v]) => [k, v.label]),
) as Record<LeaveTypeId, string>

export const LEAVE_TYPE_ACCENT: Record<LeaveTypeId, string> = Object.fromEntries(
  Object.entries(LEAVE_TYPE_STYLE).map(([k, v]) => [k, v.swatch]),
) as Record<LeaveTypeId, string>

export const LEAVE_TYPE_SHORT: Record<LeaveTypeId, string> = Object.fromEntries(
  Object.entries(LEAVE_TYPE_STYLE).map(([k, v]) => [k, v.short]),
) as Record<LeaveTypeId, string>

/** Canonical display order for leave-balance tiles — the single source shared by
 *  the home dashboard (PersonalBlock) and the manager PersonModal so the two
 *  never drift. Gender-specific (maternity/paternity) and situational (unpaid)
 *  types still only appear when applicable; this is purely the sort order. */
export const LEAVE_BALANCE_ORDER: readonly LeaveTypeId[] = [
  'annual',
  'medical',
  'hospitalization',
  'birthday',
  'marriage',
  'compassionate',
  'maternity',
  'paternity',
  'replacement',
  'unpaid',
]

/** Whether the type allows a per-day FULL/AM/PM breakdown. */
export const LEAVE_TYPE_HALF_DAY_ALLOWED: Record<LeaveTypeId, boolean> = {
  annual: true,
  medical: true,
  hospitalization: false,
  birthday: false,
  compassionate: false,
  maternity: false,
  paternity: false,
  marriage: true,
  marriage_family_care: true, // retired; mirrors marriage (moot — not applyable)
  unpaid: true,
  replacement: true,
}

/** Maximum block in calendar days for leave types whose end date is
 *  pre-filled from the start (capped at the booking window, editable down to
 *  a shorter block — see lib/fixed-duration). Undefined entries mean the
 *  employee picks both start and end dates freely.
 *  This serves as a client-side fallback — the API will also expose
 *  `fixed_duration_days`, but the frontend needs it immediately for the UX. */
export const LEAVE_TYPE_FIXED_DURATION: Partial<Record<LeaveTypeId, number>> = {
  maternity: 98,
  paternity: 7,
}

/**
 * Owner-facing status. Employees don't need to know HR is reviewing their own
 * request, so the `pending_hr_review` holding state reads as plain `pending` on
 * the requester's OWN surfaces (My Leaves, home, their own leave detail). HR /
 * managers / approvers — anyone viewing someone else's leave — always see the
 * true status.
 *
 * DISPLAY-ONLY: use this for the label/badge a requester sees, NEVER to gate an
 * action. A `pending_hr_review` leave is not owner-cancellable
 * (_CANCELLABLE_STATUSES), so action-gating must keep reading the real status.
 */
export function ownerFacingStatus(status: LeaveStatus): LeaveStatus {
  return status === 'pending_hr_review' ? 'pending' : status
}

/**
 * Display metadata for leave statuses. Keyed by `LeaveStatus` so TypeScript
 * will error if a new status is added to the schema without a matching entry.
 */
export const STATUS_META: Record<
  LeaveStatus,
  { label: string; variant: 'default' | 'info' | 'success' | 'warning' | 'danger'; icon: string }
> = {
  draft: { label: 'Draft', variant: 'default', icon: 'edit_note' },
  // HR-first review holding state (migration 0195, feature HR_DOCUMENT_REVIEW).
  pending_hr_review: { label: 'Pending HR review', variant: 'warning', icon: 'fact_check' },
  pending: { label: 'Pending', variant: 'info', icon: 'schedule' },
  escalated: { label: 'Escalated', variant: 'warning', icon: 'escalator_warning' },
  approved: { label: 'Approved', variant: 'success', icon: 'check_circle' },
  rejected: { label: 'Rejected', variant: 'danger', icon: 'cancel' },
  cancelled: { label: 'Cancelled', variant: 'default', icon: 'block' },
  // Cancellation-of-approved-leave holding state (migration 0138).
  pending_cancellation: {
    label: 'Cancellation requested',
    variant: 'warning',
    icon: 'undo',
  },
}

/**
 * Returns true when a leave type's remaining balance is exhausted AND the type
 * should be gated (hidden from apply / disabled on the balance card).
 *
 * Annual leave is NEVER gated — at zero balance it overflows to Unpaid leave,
 * which is the intended policy path. All other balance-bearing types are gated
 * when `remaining <= 0`.
 *
 * Replacement has its own empty-state logic driven by the grants cap query;
 * this helper still gates it at `remaining <= 0` so both surfaces share the
 * same rule. The cap-query path in HeroBalanceCard short-circuits before this
 * for replacement, so there is no double-handling.
 */
export function isLeaveTypeExhausted(typeId: LeaveTypeId, remaining: number): boolean {
  if (typeId === 'annual') return false
  return remaining <= 0
}

/**
 * One-line policy description per leave type, surfaced by the ⓘ affordance
 * next to the "Leave type" label on the Apply page once a type is selected.
 * Plain-English eligibility / proof summary — HR-authored copy. Partial: types
 * not selectable in the picker (e.g. `unpaid`, which engages automatically)
 * have no description.
 */
export const LEAVE_TYPE_DESCRIPTION: Partial<Record<LeaveTypeId, string>> = {
  annual: 'Your yearly leave entitlement; accrued monthly in your first year of employment.',
  medical: 'For certified illness or injury; attach your MC and submit within 48 hours of returning.',
  hospitalization: 'For hospital admission; supported by a hospital letter or discharge summary.',
  maternity:
    'Eligible for female employees being in employment for a minimum of 4 months before due date.',
  paternity:
    'Eligible for male employees with at least one year of service, following the birth of their child.',
  marriage:
    'For confirmed employees, for your own wedding; a stated reason and supporting documents are required.',
  compassionate:
    'For bereavement following the death of an immediate family member (parents, parents-in-law, spouse, siblings, grandparents or children).',
  birthday: '1 day for confirmed staff only; must be taken within your birth month.',
  replacement:
    'Earned when you have official work with a minimum of 4 hours on a rest day or public holiday; has an expiry date.',
}

/** Whether the type requires a supporting document. */
export const LEAVE_TYPE_DOC_REQUIRED: Record<LeaveTypeId, boolean> = {
  annual: false,
  medical: true,
  hospitalization: true,
  birthday: false,
  compassionate: true,
  maternity: true,
  paternity: true,
  marriage: true,
  marriage_family_care: true, // retired; mirrors marriage (moot — not applyable)
  unpaid: false,
  replacement: false,
}
